/**
 * API client for PourCRM backend.
 *
 * Auth: Bearer JWT primary (kept in memory only — never localStorage, per
 * contract's httpOnly-cookie-fallback design), httpOnly cookie fallback via
 * withCredentials. Base URL is '/api' so the Vite dev proxy (and, in prod,
 * the same-origin path route on crm.ecbtx.com) forwards to the API — this
 * sidesteps CORS entirely per the plan's single-hostname deploy.
 *
 * On 401, clears the in-memory token and redirects to /login.
 */
import axios, { type AxiosInstance, type AxiosError } from "axios";

export const apiClient: AxiosInstance = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// In-memory token store. Deliberately not persisted (localStorage/sessionStorage)
// — a page reload re-authenticates via the httpOnly cookie fallback through
// GET /api/v2/auth/me, then the token is re-set from... actually the API only
// returns access_token on login. So on reload we rely on the cookie for /me;
// mutating requests still get the Bearer header when we have it in-memory.
let accessToken: string | null = null;

export function getToken(): string | null {
  return accessToken;
}

export function setToken(token: string): void {
  accessToken = token;
}

export function clearToken(): void {
  accessToken = null;
}

export function hasToken(): boolean {
  return !!accessToken;
}

// Request interceptor: attach Bearer token when we have one in memory.
apiClient.interceptors.request.use((config) => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor: handle 401 by clearing token state and redirecting.
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401) {
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/login")) {
        clearToken();
        const returnUrl = encodeURIComponent(
          currentPath + window.location.search,
        );
        window.location.href = `/login?return=${returnUrl}`;
      }
    }
    return Promise.reject(error);
  },
);

export function isApiError(
  error: unknown,
): error is AxiosError<{ detail?: unknown }> {
  return axios.isAxiosError(error);
}

/**
 * Extract a human-readable error message from an API error.
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    // FastAPI 422 returns detail as an array of {loc, msg, type, ...} objects.
    // Never return a non-string here — a rendered object throws React error #31.
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) =>
          d && typeof d === "object" && "msg" in d
            ? String((d as { msg: unknown }).msg)
            : String(d),
        )
        .filter(Boolean);
      return msgs.join("; ") || "Validation error";
    }
    if (detail && typeof detail === "object") return JSON.stringify(detail);
    if (detail != null) return String(detail);
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
}
